import { useLayoutEffect, useRef, useState } from "react";

// Scrolls `container` so `card`'s top edge sits `topGap`px below the container's
// visible top — without overshooting past what the container can actually scroll to.
function scrollCardNearTop(container, card, topGap) {
  const containerRect = container.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  const currentOffset = cardRect.top - containerRect.top;
  container.scrollTop = Math.max(0, container.scrollTop + currentOffset - topGap);
}

// Drives an Action card's step-timeline disclosure. A timeline card mounts ALREADY expanded and, in a
// layout effect (before its entrance fade paints), scrolls the thread so the card's top lands just under
// the container's edge. The card only renders once its reply has finished typing, so the layout above it
// is already settled — which means this open-and-position happens while the card is still invisible at the
// start of its fade, and the user never sees the timeline snap open or the thread jump. Manual toggles
// after that animate normally; accepting or rejecting collapses it again.
export default function useActionStepsDisclosure({
  enabled,
  cardRef,
  scrollContainerRef,
  collapseOn,
  topGap = 6,
}) {
  // Mount expanded (for a timeline card) so the very first render already shows the full card — the layout
  // effect below then measures and positions that settled height, with no collapsed→open reflow.
  const [expanded, setExpanded] = useState(enabled);
  const [instant, setInstant] = useState(true);
  const hasPositionedRef = useRef(false);

  useLayoutEffect(() => {
    if (!enabled || hasPositionedRef.current) return;
    hasPositionedRef.current = true;

    const card = cardRef.current;
    const container = scrollContainerRef?.current;
    if (card && container) scrollCardNearTop(container, card, topGap);

    // The mount-time open is instant (no height transition, and the card is invisible anyway); re-enable
    // transitions for later, user-driven toggles once this first positioned frame has painted.
    requestAnimationFrame(() => setInstant(false));
  }, [enabled, cardRef, scrollContainerRef, topGap]);

  useLayoutEffect(() => {
    if (collapseOn) setExpanded(false);
  }, [collapseOn]);

  return {
    expanded,
    instant,
    toggle: () => setExpanded((e) => !e),
    collapse: () => setExpanded(false),
  };
}
